import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

const MyLeave = () => {
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ leave_type: '', start_date: '', end_date: '', reason: '' });

  const fetchAll = async () => {
    try {
      const [l, b, t] = await Promise.all([
        api.get('/me/leaves'),
        api.get('/me/leave-balance'),
        api.get('/leave-types'),
      ]);
      setLeaves(l.data);
      setBalance(b.data || []);
      setTypes(t.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const submit = async () => {
    if (!form.leave_type || !form.start_date || !form.end_date) {
      toast.error('Fill all required fields'); return;
    }
    const days = Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1;
    const notice = Math.round((new Date(form.start_date) - new Date().setHours(0,0,0,0)) / 86400000);
    const isEmergency = form.leave_type.toLowerCase().includes('emergency');
    if (days > 2 && notice < 7 && !isEmergency) {
      toast.error(`Leaves >2 days need 7-day notice. You have only ${notice} days.`); return;
    }
    setSubmitting(true);
    try {
      await api.post('/me/leaves', { ...form, employee_id: 'self' });
      toast.success('Leave requested');
      setDialogOpen(false);
      setForm({ leave_type: '', start_date: '', end_date: '', reason: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Leave</h1>
          <p className="text-muted-foreground">Apply for leave and track your balance</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="apply-leave-btn"><Plus size={16} className="mr-2" />Apply Leave</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Leave Type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t, i) => <SelectItem key={i} value={t.name}>{t.name} ({t.country})</SelectItem>)}
                    <SelectItem value="Emergency Leave">Emergency Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} data-testid="leave-start" /></div>
                <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} data-testid="leave-end" /></div>
              </div>
              <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} data-testid="leave-reason" /></div>
              {form.start_date && form.end_date && (() => {
                const days = Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1;
                const notice = Math.round((new Date(form.start_date) - new Date().setHours(0,0,0,0)) / 86400000);
                const isEmerg = form.leave_type.toLowerCase().includes('emergency');
                if (days > 2 && notice < 7 && !isEmerg) {
                  return <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded" data-testid="leave-warning">⚠ Leaves &gt;2 days need 7-day notice (current: {notice} days)</div>;
                }
                return <div className="text-xs text-muted-foreground">{days} day(s) · {notice} day notice</div>;
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="submit-leave-btn">{submitting && <Loader2 size={14} className="animate-spin mr-1" />}Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {balance.map((b, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{b.leave_type}</p>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold text-emerald-500">{b.remaining}</span>
                <span className="text-sm text-muted-foreground">/ {b.total}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Used: {b.used}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">My Leave History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Days</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
            <TableBody>
              {leaves.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No leave requests yet</TableCell></TableRow>}
              {leaves.map(l => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  <TableCell>{l.leave_type}</TableCell>
                  <TableCell>{l.start_date}</TableCell>
                  <TableCell>{l.end_date}</TableCell>
                  <TableCell>{l.days}</TableCell>
                  <TableCell><Badge variant={l.status === 'approved' ? 'default' : l.status === 'rejected' ? 'destructive' : 'secondary'}>{l.status}</Badge></TableCell>
                  <TableCell className="max-w-xs truncate">{l.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyLeave;
